import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const INTERESTS = [
  { key: "histoire", label: "Histoire et révolution" },
  { key: "cosmologie", label: "Cosmologie et Lwa" },
  { key: "taino", label: "Héritage taïno" },
  { key: "femmes", label: "Femmes du Vodou" },
  { key: "diaspora", label: "Diaspora et transmission" },
  { key: "resistance", label: "Résistance et marronnage" },
  { key: "langues", label: "Langue kreyòl" },
  { key: "spiritualite", label: "Spiritualité et pratique éthique" },
];

const EXPERIENCE = [
  { key: "debutant", label: "Débutant·e — je découvre" },
  { key: "curieux", label: "Curieux·se averti·e — j'ai déjà lu ou écouté" },
  { key: "praticien", label: "Praticien·ne / famille de tradition" },
];

export default function OnboardingPage() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [interests, setInterests] = useState([]);
  const [experience, setExperience] = useState("");
  const [motivation, setMotivation] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && !user) { navigate("/auth"); return null; }

  const toggle = (k) =>
    setInterests((arr) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]));

  const submit = async () => {
    if (interests.length === 0 || !experience) {
      toast.error("Choisissez au moins un intérêt et votre niveau");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/onboarding", { interests, experience, motivation });
      setUser({ ...user, onboarding: data.onboarding });
      toast.success("Bienvenue sur le chemin !");
      navigate(`/cours/${data.onboarding.recommended_course}`);
    } catch {
      toast.error("Erreur — réessayez");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto px-6 py-16 relative z-10" data-testid="onboarding-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary flex items-center gap-2"><Sparkles size={14} /> Bienvenue</p>
      <h1 className="text-4xl font-serif-display mt-2">Personnalisons votre chemin</h1>
      <p className="text-foreground/65 mt-3">Trois questions pour vous recommander la meilleure porte d'entrée dans Chimen Nanm.</p>

      <div className="mt-10">
        <p className="font-medium mb-3">1. Qu'est-ce qui vous attire le plus ? <span className="text-xs text-muted-foreground">(plusieurs choix possibles)</span></p>
        <div className="flex flex-wrap gap-2">
          {INTERESTS.map((i) => (
            <button key={i.key} onClick={() => toggle(i.key)}
              className={`text-sm px-4 py-2 rounded-full border transition-colors duration-200 ${interests.includes(i.key) ? "border-primary bg-primary/10 text-primary" : "border-primary/20 text-foreground/70 hover:border-primary/50"}`}
              data-testid={`interest-${i.key}`}>
              {i.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="font-medium mb-3">2. Où en êtes-vous ?</p>
        <div className="space-y-2">
          {EXPERIENCE.map((e) => (
            <button key={e.key} onClick={() => setExperience(e.key)}
              className={`block w-full text-left text-sm px-4 py-3 rounded-lg border transition-colors duration-200 ${experience === e.key ? "border-primary bg-primary/10" : "border-primary/15 text-foreground/70 hover:border-primary/40"}`}
              data-testid={`experience-${e.key}`}>
              {e.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="font-medium mb-3">3. Qu'est-ce qui vous amène ici ? <span className="text-xs text-muted-foreground">(optionnel)</span></p>
        <textarea rows={3} value={motivation} onChange={(e) => setMotivation(e.target.value)}
          placeholder="Reconnexion familiale, recherche, curiosité… (an Kreyòl si w vle !)"
          className="w-full bg-input border border-primary/20 rounded-lg p-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
          data-testid="motivation-input" />
      </div>

      <button onClick={submit} disabled={busy}
        className="mt-10 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#16528A] transition-colors duration-200 disabled:opacity-50"
        data-testid="onboarding-submit-btn">
        Commencer mon parcours <ArrowRight size={16} />
      </button>
    </main>
  );
}
